// ─── Buscador de terceros para los costos asociados ─────────────────────────
// Un costo asociado SIEMPRE es de alguien: de ese alguien salen el % de
// retención que se le practica y el documento con el que se le reporta la
// exógena. Mientras el campo fue texto libre, cada quien escribía el nombre a
// su manera ("Juan C. Bernal", "JUAN CARLOS BERNAL", "juan bernal") y el ERP
// creaba un tercero nuevo por cada variante: tres fichas sin cédula, sin tarifa
// y sin forma de reportar a la misma persona.
//
// Por eso aquí NO se teclea un nombre: se ELIGE un tercero ya creado. Y si no
// existe, se crea en el momento con lo mínimo que la ley exige para poder
// retenerle y reportarlo (nombre, documento, tarifa), sin salir de la orden.
import { saveData } from "../core/cloudSync.js";
import { C, REGIMENES_FISCALES } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, genId, inputStyle, normalize, parseNumber, searchFilter } from "../core/utils.js";
import { render } from "../ui/app.js";
import { registerActions } from "../ui/actions.js";
import { RETENCIONES_SERVICIO, retencionSugerida, terceroDeCosto } from "./cuentasPagar.js";
import { renderDocFormModal } from "./documentos.js";
import { renderLiquidacionModal } from "./cxp.js";

// ─── El verde de la fila resaltada ──────────────────────────────────────────
// Traslúcido a propósito: el texto de la fila (nombre, documento y tarifas) se
// sigue leyendo encima, que es lo que hay que leer para elegir. Vibrante para
// que el que está elegido se distinga de un simple paso del ratón.
const VERDE_ELEGIDO = "rgba(16, 185, 129, .42)";
const VERDE_HOVER = "rgba(16, 185, 129, .16)";

// El costo al que apunta el buscador. Dos formas, porque los costos viven en
// dos sitios: sueltos en la OP (`_conceptosCosto`) o colgados de una línea de
// la cotización (`items[i]._costosLinea`).
function costoDelContexto(ctx) {
  if (!ctx) return null;
  // Tercera forma: la ventana de LIQUIDACIÓN de la OP, que edita los mismos
  // costos por su propia copia (`state.liqConceptos`). Es la puerta por la que
  // de verdad se trabajan los costos de una orden ya completada —el formulario
  // queda bloqueado cuando la orden generó documentos—, así que sin esto el
  // tercero se seguía tecleando a mano justo donde más se usa.
  if (ctx.tipo === "liq") return (state.liqConceptos || [])[ctx.idx] || null;
  if (!state.editingDoc) return null;
  if (ctx.tipo === "concepto") {
    return (state.editingDoc._conceptosCosto || [])[ctx.idx] || null;
  }
  const it = (state.editingDoc.items || [])[ctx.i];
  return it ? ((it._costosLinea || [])[ctx.j] || null) : null;
}

// A qué ventana se vuelve al cerrar el buscador: a la que lo abrió.
function volverDe(ctx) {
  if (ctx && ctx.tipo === "liq") { renderLiquidacionModal(); return; }
  renderDocFormModal();
}

export function showTerceroPicker(ctx) {
  const c = costoDelContexto(ctx);
  state._terceroPicker = {
    ctx,
    // Se arranca con el nombre que ya tuviera el costo: al cambiar de tercero,
    // el que está puesto es el primer resultado que se quiere ver.
    q: c && c.proveedor_nombre ? String(c.proveedor_nombre) : "",
    nuevo: null
  };
  renderTerceroPickerModal();
}

// Lo que el ERP sabe del tercero, resumido: es lo que decide si se le puede
// hacer una cuenta de cobro hoy o si falta ficha.
function resumenFicha(t) {
  const partes = [];
  if (t.nit) partes.push(`CC/NIT ${t.nit}`);
  const rf = parseNumber(t.retefuente) || 0;
  const rica = parseNumber(t.reteica) || 0;
  if (rf > 0) partes.push(`RF ${rf}%`);
  if (rica > 0) partes.push(`ICA ${rica}%`);
  if (t.responsable_iva) partes.push("responsable de IVA");
  return partes.join(" · ");
}

export function renderTerceroPickerModal() {
  const d = state._terceroPicker;
  if (!d) return;
  const costo = costoDelContexto(d.ctx);
  const q = d.q || "";
  const todos = state.data.proveedores || [];
  const lista = searchFilter(todos, q, ["nombre", "nit", "ciudad", "ciudad_dian", "email", "telefono"]);
  const elegido = costo ? (costo.proveedor_nombre || "").trim() : "";

  const filas = todos.length === 0 ? `
    <div style="padding:22px;text-align:center;background:#f8fafc;border-radius:8px;color:${C.muted};">
      <div style="font-size:28px;margin-bottom:6px;">👤</div>
      <div style="font-size:12px;">Todavía no hay terceros creados</div>
      <div style="font-size:11px;margin-top:3px;">Créalo aquí abajo y queda en el maestro de proveedores.</div>
    </div>` : lista.length === 0 ? `
    <div style="padding:16px;text-align:center;color:${C.muted};">
      <div style="font-size:17px;margin-bottom:5px;">🔍</div>
      <div style="font-size:12px;">Nadie coincide con "${escapeHTML(q)}"</div>
      <div style="font-size:11px;margin-top:3px;">Si es un tercero nuevo, créalo aquí abajo.</div>
    </div>` : `
    <div id="tercero-picker-lista" style="max-height:300px;overflow-y:auto;border:1px solid ${C.border};border-radius:8px;">
      ${lista.map(t => {
        const sel = normalize(t.nombre || "") === normalize(elegido);
        const resumen = resumenFicha(t);
        const fondo = sel ? VERDE_ELEGIDO : "white";
        return `
        <div data-action="tercero-picker-elegir" data-id="${t.id}"
          style="padding:8px 10px;border-bottom:1px solid ${C.border};cursor:pointer;background:${fondo};"
          onmouseover="this.style.background='${VERDE_HOVER}'" onmouseout="this.style.background='${fondo}'">
          <div style="font-weight:700;color:${C.text};font-size:12px;">
            ${sel ? "✅ " : ""}${escapeHTML(t.nombre || "(sin nombre)")}
          </div>
          <div style="font-size:10px;color:${resumen ? C.muted : "#b45309"};margin-top:1px;">
            ${resumen ? escapeHTML(resumen)
                      : "⚠️ ficha incompleta — sin documento no se le puede retener ni reportar"}
          </div>
        </div>`;
      }).join("")}
    </div>`;

  // ─── Crear el tercero sin salir de la orden ──────────────────────────────
  // Con lo mínimo que hace falta para poder retenerle: quién es, con qué
  // documento y a qué tarifa. Lo demás se completa después en Maestros.
  const n = d.nuevo;
  const opcion = n ? (RETENCIONES_SERVICIO.find(r => r.id === n.retencion) || RETENCIONES_SERVICIO[0]) : null;
  const pctNuevo = n ? ((n.pctManual != null && n.pctManual !== "") ? parseNumber(n.pctManual) : opcion.pct) : 0;
  const formNuevo = !n ? `
    <button data-action="tercero-picker-nuevo" style="${btnStyle("#0E7490", null, true)}">＋ Crear un tercero nuevo</button>
  ` : `
    <div style="border:1px solid ${C.border};border-radius:8px;padding:10px 12px;background:#f8fafc;">
      <div style="font-size:11px;font-weight:800;color:${C.text};margin-bottom:8px;">👤 Nuevo tercero</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;">
        <div>
          <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">Nombre completo *</label>
          <input id="tp-nombre" value="${escapeHTML(n.nombre || "")}" style="${inputStyle}font-size:11.5px;"
            oninput="state._terceroPicker.nuevo.nombre=this.value"/>
        </div>
        <div>
          <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">Cédula o NIT *</label>
          <input id="tp-nit" value="${escapeHTML(n.nit || "")}" inputmode="numeric" style="${inputStyle}font-size:11.5px;"
            oninput="state._terceroPicker.nuevo.nit=this.value"/>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;">
        <div>
          <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">Régimen fiscal</label>
          <select style="${inputStyle}font-size:11.5px;cursor:pointer;"
            onchange="state._terceroPicker.nuevo.regimen=this.value;renderTerceroPickerModal();">
            ${REGIMENES_FISCALES.map(r => `<option value="${r.value}" ${(n.regimen || "no_declarante") === r.value ? "selected" : ""}>${r.label}</option>`).join("")}
          </select>
        </div>
        <div>
          <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">Ciudad / Municipio (ICA)</label>
          <input value="${escapeHTML(n.ciudad || "")}" placeholder="Ej: Bogotá D.C." style="${inputStyle}font-size:11.5px;"
            oninput="state._terceroPicker.nuevo.ciudad=this.value"/>
        </div>
      </div>
      <div style="margin-bottom:8px;">
        <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">¿Qué le presta a la empresa? — define la retención</label>
        <select style="${inputStyle}font-size:11.5px;cursor:pointer;"
          onchange="state._terceroPicker.nuevo.retencion=this.value;state._terceroPicker.nuevo.pctManual=null;renderTerceroPickerModal();">
          ${RETENCIONES_SERVICIO.map(r => `<option value="${r.id}" ${n.retencion === r.id ? "selected" : ""}>${r.label}${r.pct ? ` — ${r.pct}%` : ""}</option>`).join("")}
        </select>
        ${opcion.cita ? `<div style="font-size:9.5px;color:#0E7490;font-weight:700;margin-top:3px;">⚖️ ${escapeHTML(opcion.cita)}</div>` : ""}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;">
        <div>
          <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">% ReteFuente</label>
          <input type="number" step="0.01" min="0" max="100" value="${escapeHTML(String(pctNuevo))}"
            style="${inputStyle}font-size:11.5px;text-align:right;"
            oninput="state._terceroPicker.nuevo.pctManual=this.value"/>
        </div>
        <div>
          <label style="font-size:9.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:2px;">% ReteICA del municipio</label>
          <input type="number" step="0.001" min="0" max="100" value="${escapeHTML(n.reteica != null ? String(n.reteica) : "0")}"
            style="${inputStyle}font-size:11.5px;text-align:right;"
            oninput="state._terceroPicker.nuevo.reteica=this.value"/>
        </div>
      </div>
      ${/* Si el tercero es responsable de IVA, su servicio causa IVA y ese IVA
            es DESCONTABLE: marcarlo aquí es lo que hace que la orden de compra
            que nazca del costo lo lleve. */ ""}
      <label style="display:flex;align-items:center;gap:6px;font-size:11px;color:${C.text};cursor:pointer;margin-bottom:6px;">
        <input type="checkbox" ${n.responsable_iva ? "checked" : ""}
          onchange="state._terceroPicker.nuevo.responsable_iva=this.checked"/>
        Responsable de IVA (su servicio causa IVA descontable)
      </label>
      <label style="display:flex;align-items:center;gap:6px;font-size:11px;color:${C.text};cursor:pointer;margin-bottom:10px;"
        title="Solo si la persona firma bajo juramento que no tomará costos ni deducciones. Decide si esa declaración se imprime en su cuenta de cobro.">
        <input type="checkbox" ${n.declara383 ? "checked" : ""}
          onchange="state._terceroPicker.nuevo.declara383=this.checked"/>
        Firma la declaración del Art. 383 E.T.
      </label>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button data-action="tercero-picker-cancelar-nuevo" style="${btnStyle(null, "secondary", true)}">Cancelar</button>
        <button data-action="tercero-picker-crear" style="${btnStyle("#0E7490", null, true)}">✅ Crear y elegir</button>
      </div>
    </div>`;

  const totalCosto = costo
    ? Math.round((parseNumber(costo.cantidad) || 0) * (parseNumber(costo.valor_tercero) || 0))
    : 0;

  state._terceroPickerFocus = !!n;
  state.modal = `
    <div data-action="prevent-close" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1160;padding:11px;">
      <div style="background:white;border-radius:14px;width:600px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.22);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};background:#f8fafc;display:flex;justify-content:space-between;align-items:flex-start;gap:10px;">
          <div>
            <div style="font-size:15px;font-weight:800;color:${C.text};">🔍 ¿De quién es este costo?</div>
            <div style="font-size:11px;color:${C.muted};margin-top:3px;line-height:1.5;">
              ${costo && (costo.concepto || "").trim()
                ? `<b>${escapeHTML(costo.concepto)}</b>${totalCosto > 0 ? ` · ${fmtCOP(totalCosto)}` : ""} — `
                : ""}elige un tercero ya creado. De su ficha salen el <b>% de retención</b> y el
              documento con el que se le reporta a la DIAN.
            </div>
          </div>
          <button data-action="tercero-picker-cerrar" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px 14px;">
          <div style="position:relative;margin-bottom:9px;">
            <span style="position:absolute;left:11px;top:50%;transform:translateY(-50%);font-size:13px;color:${C.muted};">🔍</span>
            <input id="tercero-picker-q" type="text" value="${escapeHTML(q)}" autocomplete="off"
              placeholder="Buscar por nombre, cédula/NIT o ciudad..."
              style="${inputStyle}padding-left:34px;"
              oninput="state._terceroPicker.q=this.value;renderTerceroPickerModal();"/>
          </div>
          ${q && todos.length ? `<div style="font-size:10.5px;color:${C.muted};margin-bottom:8px;">Mostrando ${lista.length} de ${todos.length}</div>` : ""}
          ${filas}
          ${elegido ? `
            <div style="margin-top:9px;display:flex;justify-content:space-between;align-items:center;gap:8px;font-size:11px;color:${C.muted};">
              <span>Ahora este costo es de <b style="color:${C.text};">${escapeHTML(elegido)}</b></span>
              <button data-action="tercero-picker-quitar" style="${btnStyle(null, "secondary", true)}">Quitar el tercero</button>
            </div>` : ""}
          <div style="margin-top:12px;padding-top:11px;border-top:1px solid ${C.border};">
            ${formNuevo}
          </div>
        </div>
      </div>
    </div>`;
  render();

  setTimeout(() => {
    if (window.innerWidth <= 768) return;
    // Con el formulario abierto el foco va al nombre del tercero nuevo; si no,
    // al buscador, que es donde se sigue escribiendo.
    const el = document.getElementById(state._terceroPickerFocus ? "tp-nombre" : "tercero-picker-q");
    if (el) { el.focus(); if (el.setSelectionRange) el.setSelectionRange(el.value.length, el.value.length); }
  }, 0);
}

// Deja el tercero puesto en el costo. Se guarda el nombre (que es lo que
// imprimen los documentos) Y el id, que es el vínculo real con la ficha.
function ponerTercero(ctx, tercero) {
  const c = costoDelContexto(ctx);
  if (!c) return;
  c.proveedor_nombre = tercero ? tercero.nombre : "";
  if (tercero) c.proveedor_id = tercero.id; else delete c.proveedor_id;
}

registerActions({
  "buscar-tercero-costo": (d) => {
    showTerceroPicker({ tipo: "concepto", idx: parseInt(d.idx, 10) });
  },
  "buscar-tercero-costo-linea": (d) => {
    showTerceroPicker({ tipo: "linea", i: parseInt(d.idx, 10), j: parseInt(d.sub, 10) });
  },
  "quitar-tercero-costo": (d) => {
    ponerTercero({ tipo: "concepto", idx: parseInt(d.idx, 10) }, null);
    renderDocFormModal();
  },
  // La misma pareja, desde la ventana de liquidación de la OP.
  "buscar-tercero-liq": (d) => {
    showTerceroPicker({ tipo: "liq", idx: parseInt(d.idx, 10) });
  },
  "quitar-tercero-liq": (d) => {
    ponerTercero({ tipo: "liq", idx: parseInt(d.idx, 10) }, null);
    renderLiquidacionModal();
  },
  "quitar-tercero-costo-linea": (d) => {
    ponerTercero({ tipo: "linea", i: parseInt(d.idx, 10), j: parseInt(d.sub, 10) }, null);
    renderDocFormModal();
  },
  "tercero-picker-elegir": (d) => {
    const p = state._terceroPicker;
    if (!p) return;
    const t = (state.data.proveedores || []).find(x => x.id === d.id);
    if (!t) return;
    ponerTercero(p.ctx, t);
    const ctx = p.ctx;
    state._terceroPicker = null;
    volverDe(ctx);
  },
  "tercero-picker-quitar": () => {
    const p = state._terceroPicker;
    if (!p) return;
    ponerTercero(p.ctx, null);
    const ctx = p.ctx;
    state._terceroPicker = null;
    volverDe(ctx);
  },
  "tercero-picker-cerrar": () => {
    const ctx = state._terceroPicker && state._terceroPicker.ctx;
    state._terceroPicker = null;
    volverDe(ctx);
  },
  "tercero-picker-nuevo": () => {
    const p = state._terceroPicker;
    if (!p) return;
    const c = costoDelContexto(p.ctx);
    // El nombre tecleado en el buscador es casi siempre el del tercero que se
    // va a crear, y la retención se propone por el concepto del costo.
    p.nuevo = {
      nombre: (p.q || "").trim(),
      nit: "", regimen: "no_declarante", ciudad: "",
      retencion: retencionSugerida(c ? c.concepto : ""),
      pctManual: null, reteica: 0, responsable_iva: false, declara383: false
    };
    renderTerceroPickerModal();
  },
  "tercero-picker-cancelar-nuevo": () => {
    if (state._terceroPicker) state._terceroPicker.nuevo = null;
    renderTerceroPickerModal();
  },
  "tercero-picker-crear": () => {
    const p = state._terceroPicker;
    if (!p || !p.nuevo) return;
    const n = p.nuevo;
    const nombre = (n.nombre || "").trim();
    const nit = (n.nit || "").trim();
    if (!nombre || !nit) {
      alert("El nombre y la cédula o NIT son obligatorios: sin ellos no hay tarifa que aplicarle ni con qué reportarlo a la DIAN.");
      return;
    }
    const opcion = RETENCIONES_SERVICIO.find(r => r.id === n.retencion) || RETENCIONES_SERVICIO[0];
    const pct = (n.pctManual != null && n.pctManual !== "") ? parseNumber(n.pctManual) : opcion.pct;
    // Si ya existe alguien con ese nombre, se COMPLETA su ficha en vez de
    // duplicarla: el maestro de terceros es uno solo.
    const yaEsta = terceroDeCosto(nombre);
    const ficha = {
      ...(yaEsta || { id: genId(), dias_pago: 0 }),
      nombre, nit,
      regimen_fiscal: n.regimen || "no_declarante",
      ciudad_dian: n.ciudad || (yaEsta || {}).ciudad_dian || "",
      responsable_iva: !!n.responsable_iva,
      retefuente: pct,
      reteica: parseNumber(n.reteica) || 0,
      reteiva: parseNumber((yaEsta || {}).reteiva) || 0,
      declara_383: !!n.declara383 || !!opcion.declara383
    };
    saveData("proveedores", yaEsta
      ? (state.data.proveedores || []).map(x => x.id === yaEsta.id ? ficha : x)
      : [...(state.data.proveedores || []), ficha]);
    ponerTercero(p.ctx, ficha);
    const ctx = p.ctx;
    state._terceroPicker = null;
    volverDe(ctx);
  }
});
